import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {HelpersService} from "../../../../../../../common/services/helpers.service";
import {ToastService} from "../../../../../../../common/services/toast.service";
import {Api, ApiService} from "../../../../../../../common/services/api.service";
import {Modal} from "../../../../../../services/modal.service";
import {SpinnerService} from "../../../../../../../common/services/spinner.service";
import {User} from "../../../../../../../common/interfaces/user.interface";
import {distinctUntilChanged, takeUntil} from "rxjs/operators";
import {Api3Service} from "../../../../../../../common/services/api3.service";


@Component({
    selector: "section-inventory-count-form",
    templateUrl: "form.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WarehouseInventoryCountFormComponent implements OnInit, OnDestroy {

    private destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public scanTypes: { name: string, value: string }[] = [
        { name: "Pallet", value: "pallet" },
        { name: "Box", value: "box" },
        { name: "Serial", value: "serial" },
        { name: "Batch", value: "batch" },
        { name: "Item", value: "item" }
    ];

    /**
     * Product types stored in part_master.properties.type. Only Annapurna maintains them,
     * so the select below is shown for that partner alone.
     */
    public productTypes: { name: string, value: string }[] = [
        { name: "Card", value: "CARD" },
        { name: "ASIC", value: "ASIC" },
        { name: "Components", value: "COMPONENTS" },
        { name: "System", value: "SYSTEM" }
    ];

    public formGroup: FormGroup = new FormGroup({
        ref: new FormControl(null, [Validators.required]),
        partner_id: new FormControl(null, [Validators.required]),
        part_master_id: new FormControl([]),
        product_type: new FormControl([]),
        available_scan_types: new FormControl(this.scanTypes.map(type => type.value))
    });

    public partner: FormControl = new FormControl(null, [Validators.required]);

    public modal: Modal.IModal;

    public partners: User.IPartner[] = [];

    public partmasters: { name: string, value: any }[] = [];

    public get hasProductTypes(): boolean {
        return this.partner.value?.slug === "annapurna_labs";
    }

    public constructor(
        private apiService: ApiService,
        private api3Service: Api3Service,
        private toastService: ToastService,
        private changeDetectorRef: ChangeDetectorRef,
        private spinnerService: SpinnerService
    ) {
    }

    /**
     * Get partners list
     */
    private async getPartners(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get,
            ["warehouse", "partners"]);
        if (data) {
            this.partners = data.map((partner: User.IPartner): any => {
                return {
                    name: partner.display_name,
                    value: partner
                };
            });
            this.changeDetectorRef.markForCheck();
        }
        this.spinnerService.hide();
    }

    /**
     * Get part masters list
     */
    private async getPartMasters(partner_id: number): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.api3Service.get(`${this.modal.params.state.section}/part-masters`,
            {
                partners: [partner_id],
                data_structure: "select",
                in_stock: true,
            });
        if (data) {
            this.partmasters = data;
            this.changeDetectorRef.markForCheck();

            if (this.partmasters.length < 1) {
                this.toastService.show("No items in stock fo selected partner", "warning");
            }
        }
        this.spinnerService.hide();
    }

    /**
     * Submit form
     */
    public async submit(): Promise<any> {
        this.spinnerService.show();
        const asyncVal: string = HelpersService.randomString();
        this.apiService.setHeaders({
            async: asyncVal
        });
        const {type, message}: Api.IResponse = await this.apiService.request(Api.EMethod.Post,
            ["inventory", "counts"], this.formGroup.value);

        if (type as string === "success") {
            this.toastService.show(message, "success");
            this.modal.response.emit({
                name: "value",
                value: asyncVal
            });
        }
        this.spinnerService.hide();
    }

    public ngOnInit(): void {
        this.getPartners();

        this.partner.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
            .subscribe((value: User.IPartner): void => {
                this.formGroup.get("product_type").setValue([]);

                if (value) {
                    this.getPartMasters(value.id);
                    this.formGroup.get("partner_id").setValue(value.id);
                } else {
                    this.formGroup.get("partner_id").setValue(null);
                    this.formGroup.get("part_master_id").setValue([]);
                }
            });

        // The two narrow the same thing, so picking a type drops any explicit item selection.
        this.formGroup.get("product_type").valueChanges
            .pipe(takeUntil(this.destroy$), distinctUntilChanged())
            .subscribe((value: string[]): void => {
                if (value && value.length) {
                    this.formGroup.get("part_master_id").setValue([]);
                    this.changeDetectorRef.markForCheck();
                }
            });
    }


    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
